'use strict'

export interface Facets {
  getFormsValues: () => {
    capacity: number
    budgetRange: { from: number; to: number } | null
    accommodationTypes: string[]
    properties: string[]
    filters: string[]
  }
  isAccommodationMatchingForms: (accommodationId: string) => boolean
  isAccommodationMatchingProperties: (accommodationId: string, properties: string[]) => boolean
  isAccommodationMatchingAccommodationType: (accommodationId: string, types: string[]) => boolean
  isAccommodationMatchingFilters: (accommodationId: string, filters: string[]) => boolean
  hasOffer: (accommodationId: string) => boolean
  getAccommodationsMatchingForms: () => string[]
  getAccommodationTypesList: () => { name: string; types: string[] }[]
  getPropertiesList: () => string[]
  getFiltersList: () => string[]
  sortAccommodations: (callback?: () => void) => void
  updateFacets: (matchingAccommodations: string[]) => void
  updateFilter: (el: HTMLInputElement | null, value: number) => void
}

export default {
  getFormsValues: function () {
    const capacityEl = document.getElementById('capacity') as HTMLInputElement
    const capacity = parseInt(capacityEl.value) || 0
    const budgetInput = document.querySelector<HTMLInputElement>('[data-filter="budgets"] input:checked')
    const budgetRange = budgetInput ? budgetInput.value.split(':') : null
    const accommodationTypes = Array.from(document.querySelectorAll('[data-filter="distribution-types"] input:checked'), (e) =>
      e.getAttribute('data-distribution-types')
    )
      // @ts-ignore
      .map((concatenatedTypes) => concatenatedTypes.split(';'))
      // flatten
      .reduce((acc, val) => acc.concat(val), [])

    const properties: string[] = []
    const propertiesCheckbox = document.querySelectorAll<HTMLInputElement>('input[name^="accommodation_filters"][data-type="property"]:checked')

    propertiesCheckbox.forEach((checkbox) => {
      properties.push(checkbox.value)
    })

    const filters: string[] = []
    const filtersCheckbox = document.querySelectorAll<HTMLInputElement>('input[name^="accommodation_filters"][data-type="filter"]:checked')

    filtersCheckbox.forEach((checkbox) => {
      filters.push(checkbox.getAttribute('data-name') as string)
    })

    return {
      capacity,
      budgetRange: budgetRange
        ? {
            from: parseInt(budgetRange[0] as string),
            to: parseInt(budgetRange[1] as string),
          }
        : null,
      accommodationTypes,
      properties,
      filters,
    }
  },

  isAccommodationMatchingForms: function (accommodationId) {
    const formsValues = this.getFormsValues()
    const requiredCapacity = formsValues.capacity
    const requiredBudgetRange = formsValues.budgetRange
    const requiredAccommodationTypes = formsValues.accommodationTypes
    const requiredProperties = formsValues.properties
    const requiredFilters = formsValues.filters

    if (!(accommodationId in window.accommodationsData)) {
      return false
    }
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return
    }

    const accommodationPrice = Object.prototype.hasOwnProperty.call(accommodationData, 'price') ? accommodationData.price : null
    const accommodationCapacity = Object.prototype.hasOwnProperty.call(accommodationData, 'capacity') ? accommodationData.capacity : null
    const accommodationTypes = Object.prototype.hasOwnProperty.call(accommodationData, 'accommodationTypes')
      ? accommodationData.accommodationTypes
      : null

    if (!accommodationPrice || !accommodationCapacity || !accommodationTypes) {
      return
    }

    const matchCapacity = accommodationCapacity >= requiredCapacity
    const matchAccommodationTypes =
      requiredAccommodationTypes.length > 0 ? requiredAccommodationTypes.filter((x) => accommodationTypes.includes(x)).length !== 0 : true
    const matchProperties = this.isAccommodationMatchingProperties(accommodationId, requiredProperties)
    const matchFilters = this.isAccommodationMatchingFilters(accommodationId, requiredFilters)

    const matchBudget = requiredBudgetRange ? accommodationPrice >= requiredBudgetRange.from && accommodationPrice <= requiredBudgetRange.to : true

    return matchBudget && matchCapacity && matchAccommodationTypes && matchProperties && matchFilters
  },

  isAccommodationMatchingProperties: function (accommodationId, properties) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationProperties = Object.prototype.hasOwnProperty.call(accommodationData, 'properties') ? accommodationData.properties : null

    if (!accommodationProperties) {
      return false
    }

    return properties.every((elem) => accommodationProperties.indexOf(elem) > -1)
  },

  isAccommodationMatchingAccommodationType: function (accommodationId, types) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationTypes = Object.prototype.hasOwnProperty.call(accommodationData, 'accommodationTypes')
      ? accommodationData.accommodationTypes
      : null

    if (!accommodationTypes) {
      return false
    }

    return types.filter((x) => accommodationTypes.includes(x)).length > 0
  },

  isAccommodationMatchingFilters: function (accommodationId, filters) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationFilters = Object.prototype.hasOwnProperty.call(accommodationData, 'filters') ? accommodationData.filters : null

    if (!accommodationFilters) {
      return false
    }

    return filters.every((elem) => accommodationFilters.indexOf(elem) > -1)
  },

  hasOffer: function (accommodationId) {
    const accommodationData = window.accommodationsData[accommodationId]

    if (!accommodationData) {
      return false
    }

    const accommodationPrice = Object.prototype.hasOwnProperty.call(accommodationData, 'price') ? accommodationData.price : null
    return accommodationPrice !== null
  },

  getAccommodationsMatchingForms: function () {
    return window.accommodationsOrder.filter((id) => this.isAccommodationMatchingForms(id))
  },

  getAccommodationTypesList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="accommodation_filters"][data-type="distribution-type"]')).map(
      (input) => ({
        name: input.dataset.name,
        types: input.getAttribute('data-distribution-types')?.split(';'),
      })
    )
  },

  getPropertiesList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="accommodation_filters"][data-type="property"]')).map(
      (input) => input.value
    )
  },

  getFiltersList: function () {
    return Array.from(document.querySelectorAll<HTMLInputElement>('input[name^="accommodation_filters"][data-type="filter"]')).map((input) =>
      input.getAttribute('data-name')
    )
  },

  sortAccommodations: function (callback) {
    const matchList = document.querySelector<HTMLElement>('#accommodations-match-filter .results__list')
    const noMatchList = document.querySelector<HTMLElement>('#accommodations-others .results__list')
    const noOfferList = document.querySelector<HTMLElement>('#accommodations-no-offers .results__list')
    let matchingAccommodations: string[] = []

    if (matchList && noMatchList && window.accommodationsData !== undefined && window.accommodationsOrder !== undefined) {
      matchingAccommodations = this.getAccommodationsMatchingForms()
      for (const accommodationId of window.accommodationsOrder) {
        const accommodationNode = document.querySelector('[data-id="' + accommodationId + '"]')
        if (accommodationNode === null) {
          continue
        }

        if (!this.hasOffer(accommodationId)) {
          noOfferList && noOfferList.appendChild(accommodationNode)
        } else if (matchingAccommodations.includes(accommodationId)) {
          matchList && matchList.appendChild(accommodationNode)
        } else {
          noMatchList && noMatchList.appendChild(accommodationNode)
        }
      }

      if (Array.prototype.slice.call(noMatchList.children).length === 0) {
        document.getElementById('accommodations-others')?.classList.add('results--hidden')
      } else {
        document.getElementById('accommodations-others')?.classList.remove('results--hidden')
      }
      if (Array.prototype.slice.call(matchList.children).length === 0) {
        document.getElementById('accommodations-match-filter')?.classList.add('results--hidden')
      } else {
        document.getElementById('accommodations-match-filter')?.classList.remove('results--hidden')
      }
    }

    if (callback) {
      callback()
    }

    this.updateFacets(matchingAccommodations)
  },

  updateFacets: function (matchingAccommodations) {
    if (matchingAccommodations.length === 0) {
      matchingAccommodations = this.getAccommodationsMatchingForms()
    }

    for (const input of this.getAccommodationTypesList()) {
      const { types, name } = input

      let facetValue = 0
      for (const accommodationId of matchingAccommodations) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingAccommodationType(accommodationId, types)) {
          facetValue++
        }
      }

      this.updateFilter(document.querySelector('input[name^="accommodation_filters"][data-name="' + name + '"]'), facetValue)
    }

    for (const propertyKey of this.getPropertiesList()) {
      let facetValue = 0
      for (const accommodationId of matchingAccommodations) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingProperties(accommodationId, [propertyKey])) {
          facetValue++
        }
      }

      this.updateFilter(document.querySelector('input[name^="accommodation_filters"][value="' + propertyKey + '"]'), facetValue)
    }

    for (const filterKey of this.getFiltersList()) {
      let facetValue = 0
      for (const accommodationId of matchingAccommodations) {
        if (this.hasOffer(accommodationId) && this.isAccommodationMatchingFilters(accommodationId, [filterKey])) {
          facetValue++
        }
      }

      this.updateFilter(document.querySelector('input[name^="accommodation_filters"][data-name="' + filterKey + '"]'), facetValue)
    }
  },

  updateFilter: function (el, value) {
    if (!el) {
      return
    }

    const labelEl = el.nextElementSibling

    if (!labelEl) {
      return
    }

    const valueEl = labelEl.querySelector('.filter__value')

    if (!valueEl) {
      return
    }

    valueEl.textContent = value.toString()

    if (labelEl && value === 0 && !el.checked) {
      el.setAttribute('disabled', 'disabled')
      labelEl.classList.add('filter--disabled')
    } else {
      el.removeAttribute('disabled')
      labelEl.classList.remove('filter--disabled')
    }
  },
} as Facets
